import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useActivityContext } from '../index';
import { API_CONFIG } from '../../../../../config/api';

const PartE = () => {
  const { activity } = useActivityContext();
  const [courses, setCourses] = useState(
    Array(10).fill(null).map(() => ({ course_name: '', hours: 0, completed: false }))
  );
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (activity?.my_response_part_e?.courses) {
      const savedCourses = activity.my_response_part_e.courses;
      const paddedCourses = [...savedCourses];
      while (paddedCourses.length < 10) {
        paddedCourses.push({ course_name: '', hours: 0, completed: false });
      }
      setCourses(paddedCourses.slice(0, 10));
    }
  }, [activity]);

  const saveChanges = async (updatedCourses) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('accessToken');
      const apiUrl = API_CONFIG.BASE_URL;

      await axios.put(
        `${apiUrl}/secondary/user/domain-activities/${activity.activity_id}/part-e/student-fields`,
        { courses: updatedCourses },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSaveMessage('Saved ✓');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (error) {
      console.error('Error saving:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleCourseChange = (index, field, value) => {
    const updatedCourses = [...courses];
    updatedCourses[index] = {
      ...updatedCourses[index],
      [field]: field === 'hours' ? parseInt(value) || 0 : value
    };
    setCourses(updatedCourses);

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveChanges(updatedCourses), 1000);
  };

  const getTotalHours = () => {
    return courses.reduce((sum, course) => sum + (course.hours || 0), 0);
  };

  const rowLabels = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j'];

  return (
    <div style={{
      maxWidth: '1400px',       // Increased from 1200px for wider layout
      margin: '0 auto',
      padding: '40px',
      backgroundColor: 'white',
      fontFamily: "'Poppins', Arial, sans-serif",  // Updated to Poppins
      position: 'relative',
      boxSizing: 'border-box'
    }}>
      {/* Save Indicator */}
      {saveMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: saving ? '#FFC107' : '#4CAF50',
          color: saving ? 'black' : 'white',
          padding: '10px 20px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontWeight: 'bold'
        }}>
          {saveMessage}
        </div>
      )}

      {/* Header: PART E */}
      <div style={{
        color: '#FF8C42',
        fontSize: '28px',
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: '10px',
        letterSpacing: '2px'
      }}>
        PART E
      </div>

      {/* Title: Learning through Online Courses */}
      <div style={{
        color: '#2C5F7E',
        fontSize: '24px',
        fontWeight: 'bold',
        textAlign: 'left',
        marginBottom: '25px'
      }}>
        Learning through Online Courses
      </div>

      {/* Table */}
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        border: '2px solid #888',
        marginBottom: '40px'
      }}>
        {/* Table Header - "Online Course" spanning row */}
        <thead>
          <tr style={{ backgroundColor: '#F4CD8A' }}>
            <th colSpan={4} style={{
              padding: '12px',
              textAlign: 'left',
              fontWeight: 'bold',
              fontSize: '16px',
              color: '#333',
              border: '1px solid #888'
            }}>
              Online Course
            </th>
          </tr>
          {/* Column Headers */}
          <tr style={{ backgroundColor: '#F4E4B8' }}>
            <th style={{
              width: '60px',
              padding: '10px',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 'normal',
              color: '#333',
              border: '1px solid #888',
              backgroundColor: '#FFF9E6'
            }}></th>
            <th style={{
              padding: '10px',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#333',
              border: '1px solid #888'
            }}>
              Course
            </th>
            <th style={{
              width: '180px',
              padding: '10px',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#333',
              border: '1px solid #888'
            }}>
              Number of Hours
            </th>
            <th style={{
              width: '150px',
              padding: '10px',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#333',
              border: '1px solid #888'
            }}>
              Completed (✓)
            </th>
          </tr>
        </thead>

        {/* Table Body - 10 rows */}
        <tbody>
          {courses.map((course, index) => (
            <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#FFFEF8' : '#FFF9E6' }}>
              {/* Row Label */}
              <td style={{
                padding: '10px',
                textAlign: 'center',
                fontSize: '14px',
                color: '#333',
                border: '1px solid #888',
                fontWeight: 'bold'
              }}>
                {rowLabels[index]})
              </td>

              {/* Course Name Input */}
              <td style={{
                padding: '0',
                border: '1px solid #888'
              }}>
                <input
                  type="text"
                  value={course.course_name}
                  onChange={(e) => handleCourseChange(index, 'course_name', e.target.value)}
                  placeholder="Enter course name..."
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    fontSize: '14px',
                    color: '#333',
                    outline: 'none'
                  }}
                />
              </td>

              {/* Hours Input */}
              <td style={{
                padding: '0',
                border: '1px solid #888',
                textAlign: 'center'
              }}>
                <input
                  type="number"
                  min="0"
                  value={course.hours || ''}
                  onChange={(e) => handleCourseChange(index, 'hours', e.target.value)}
                  placeholder="0"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    fontSize: '14px',
                    color: '#333',
                    textAlign: 'center',
                    outline: 'none'
                  }}
                />
              </td>

              {/* Completed Checkbox */}
              <td style={{
                padding: '10px',
                border: '1px solid #888',
                textAlign: 'center'
              }}>
                <input
                  type="checkbox"
                  checked={course.completed}
                  onChange={(e) => handleCourseChange(index, 'completed', e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>

        {/* Table Footer - Total Hours */}
        <tfoot>
          <tr style={{ backgroundColor: '#F4CD8A' }}>
            <td style={{
              padding: '12px',
              border: '1px solid #888'
            }}></td>
            <td style={{
              padding: '12px',
              textAlign: 'right',
              fontWeight: 'bold',
              fontSize: '16px',
              color: '#333',
              border: '1px solid #888'
            }}>
              TOTAL HOURS
            </td>
            <td style={{
              padding: '12px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '16px',
              color: '#333',
              border: '1px solid #888'
            }}>
              {getTotalHours()}
            </td>
            <td style={{
              padding: '12px',
              border: '1px solid #888'
            }}></td>
          </tr>
        </tfoot>
      </table>

      {/* Page Number */}
      <div style={{
        textAlign: 'center',
        fontSize: '14px',
        color: '#666',
        fontWeight: 'bold',
        marginTop: '30px'
      }}>
        38
      </div>

    </div>
  );
};

export default PartE;
